import { useState, useEffect } from 'react';
import { message } from 'antd';
import ScoreSlider from './ScoreSlider';
import {
    IconArrowL, IconArrowR, IconCheck, IconMusic, IconClock,
    IconPdf, IconPlay, IconUser, IconPeople, IconMinus, IconPlus,
    IconChevD, IconWarn, initials
} from './Icons';

/**
 * Convert videoDuration (stored in seconds) to mm:ss display format
 */
const formatVideoDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '—';
    const totalSec = Math.round(Number(seconds));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/**
 * Assessment Form — full-screen scoring view matching the APCS Scoring Platform prototype.
 * Receives a single registrant, handles score/comment editing, and calls onSave.
 */
const AssessmentForm = ({
    registrant,
    existingScore,
    onBack,
    onSave,
    saving,
    onOpenVideo,
    onOpenPDF,
    loggedInUser,
    pendingQueue,
    onNext,
}) => {
    const performer = registrant.performers?.[0];
    const name = performer?.fullName ||
        `${performer?.firstName || ''} ${performer?.lastName || ''}`.trim();
    const isEnsemble = registrant.PerformanceCategory === 'Ensemble';

    // Score state
    const [score, setScore] = useState(existingScore?.score ?? 75);
    const [minus, setMinus] = useState(0);
    const [feedback, setFeedback] = useState(existingScore?.comment || '');
    const [comments, setComments] = useState(existingScore?.panelComment || '');
    const [showMembers, setShowMembers] = useState(false);

    const final = Math.max(0, score - minus);

    // Reset when registrant changes
    useEffect(() => {
        setScore(existingScore?.score ?? 75);
        setMinus(0);
        setFeedback(existingScore?.comment || '');
        setComments(existingScore?.panelComment || '');
    }, [registrant.id]);

    const handleSave = () => {
        if (score === null || score === undefined) {
            message.warning('Please enter a score before saving');
            return;
        }
        if (final < 0 || final > 100) {
            message.error('Final score must be between 0 and 100');
            return;
        }
        onSave(registrant, {
            score: final,
            comment: feedback,
            panelComment: comments,
        });
    };

    const QUICK_SCORES = [60, 70, 80, 90, 95];
    const MINUS_PRESETS = [0, 1, 2, 5];

    return (
        <div style={{ animation: 'jdScreenIn 0.22s cubic-bezier(.2,.8,.3,1)', flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Breadcrumb */}
            <div className="jd-crumbs">
                <a onClick={onBack}>Dashboard</a>
                <span className="jd-crumb-sep">/</span>
                <span className="jd-crumb-current">{name}</span>
                <div className="jd-grow" />
            </div>

            {/* Compact info strip */}
            <div className="jd-info-strip">
                <div className="jd-info-strip-left">
                    <span className="jd-avatar lg">{initials(name)}</span>
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 18, letterSpacing: '-0.01em' }}>{name}</div>
                        <div className="jd-row" style={{ gap: 6, marginTop: 6 }}>
                            <span className="jd-pill">
                                {isEnsemble ? <IconPeople size={11} /> : <IconUser size={11} />}
                                {isEnsemble
                                    ? `Group · ${registrant.performers?.length || 0} members`
                                    : 'Individual'}
                            </span>
                            <span className="jd-pill">{registrant.ageCategory || registrant.competitionCategory}</span>
                        </div>
                    </div>
                </div>
                <div className="jd-info-strip-right">
                    {registrant.repertoire && (
                        <span><IconMusic size={13} style={{ color: 'var(--jd-ink-3)' }} /> {registrant.repertoire}</span>
                    )}
                    {registrant.videoDuration && (
                        <span style={{ marginLeft: 8, color: 'var(--jd-ink-3)', fontSize: 13 }}>
                            <IconClock size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                            {formatVideoDuration(registrant.videoDuration)}
                        </span>
                    )}
                    <button
                        className="jd-link"
                        onClick={() => onOpenPDF(registrant.pdfRepertoireS3Link)}
                        disabled={!registrant.pdfRepertoireS3Link}
                        style={{ opacity: registrant.pdfRepertoireS3Link ? 1 : 0.4, marginLeft: 12 }}
                    >
                        <IconPdf size={13} /> Sheet music
                    </button>
                    <button
                        className="jd-link"
                        onClick={() => onOpenVideo(registrant.videoPerformanceS3Link)}
                        disabled={!registrant.videoPerformanceS3Link}
                        style={{ opacity: registrant.videoPerformanceS3Link ? 1 : 0.4, marginLeft: 12, marginRight: 4 }}
                    >
                        <IconPlay size={13} /> Watch video
                    </button>
                    {isEnsemble && registrant.performers?.length > 1 && (
                        <div style={{ position: 'relative' }}>
                            <button
                                className="jd-btn sm ghost"
                                onClick={() => setShowMembers(s => !s)}
                            >
                                Members ({registrant.performers.length}) <IconChevD size={12} />
                            </button>
                            {showMembers && (
                                <div className="jd-box" style={{
                                    position: 'absolute', right: 0, top: 'calc(100% + 4px)',
                                    padding: 12, minWidth: 200, zIndex: 4,
                                    boxShadow: '0 8px 20px rgba(0,0,0,.08)'
                                }}>
                                    {registrant.performers.map((m, i) => (
                                        <div key={i} className="jd-row" style={{ gap: 8, padding: '4px 0', fontSize: 12 }}>
                                            <span className="jd-avatar sm">
                                                {initials(m.fullName || `${m.firstName} ${m.lastName}`)}
                                            </span>
                                            {m.fullName || `${m.firstName} ${m.lastName}`}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Form body */}
            <div style={{ padding: '24px 32px', maxWidth: 980, margin: '0 auto', width: '100%', flex: 1 }}>
                {/* Scoring panel */}
                <div className="jd-box" style={{ padding: 24 }}>
                    <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        {/* Overall score */}
                        <div style={{ flex: '1 1 200px' }}>
                            <label className="jd-label" htmlFor="score-input">Overall score</label>
                            <div className="jd-row" style={{ alignItems: 'center', gap: 10, marginTop: 8 }}>
                                <input
                                    id="score-input"
                                    type="number"
                                    className="jd-input"
                                    min={0} max={100} step={1}
                                    value={score}
                                    onChange={e => {
                                        const raw = e.target.value;
                                        if (raw === '') { setScore(0); return; }
                                        const v = parseInt(raw, 10);
                                        if (!isNaN(v)) setScore(Math.max(0, Math.min(100, v)));
                                    }}
                                    style={{ width: 110, fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                                />
                                <span style={{ fontSize: 14, color: 'var(--jd-ink-3)' }}>/ 100</span>
                            </div>
                            {/* Quick set chips */}
                            <div className="jd-row" style={{ gap: 4, marginTop: 10, flexWrap: 'wrap' }}>
                                <span className="jd-label" style={{ marginRight: 4, fontSize: 10 }}>Quick set</span>
                                {QUICK_SCORES.map(n => (
                                    <button
                                        key={n}
                                        className={`jd-chip ${score === n ? 'active' : ''}`}
                                        onClick={() => setScore(n)}
                                    >{n}</button>
                                ))}
                            </div>
                        </div>

                        {/* Minus points */}
                        <div style={{ flex: '1 1 200px' }}>
                            <div className="jd-label">
                                Minus points <span style={{ textTransform: 'none', letterSpacing: 0, color: 'var(--jd-ink-4)', marginLeft: 4 }}>· deduction</span>
                            </div>
                            <div className="jd-row" style={{ gap: 8, marginTop: 8 }}>
                                <button className="jd-btn icon-btn" onClick={() => setMinus(Math.max(0, minus - 1))} disabled={minus <= 0}>
                                    <IconMinus size={14} />
                                </button>
                                <input
                                    type="number"
                                    className="jd-input"
                                    min={0} max={50} step={1}
                                    value={minus}
                                    onChange={e => {
                                        const raw = e.target.value;
                                        if (raw === '') { setMinus(0); return; }
                                        const v = parseInt(raw, 10);
                                        if (!isNaN(v)) setMinus(Math.max(0, Math.min(50, v)));
                                    }}
                                    style={{ width: 80, textAlign: 'center', fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                                />
                                <button className="jd-btn icon-btn" onClick={() => setMinus(Math.min(50, minus + 1))}>
                                    <IconPlus size={14} />
                                </button>
                            </div>
                            <div className="jd-row" style={{ gap: 4, marginTop: 10, flexWrap: 'wrap' }}>
                                <span className="jd-label" style={{ marginRight: 4, fontSize: 10 }}>Presets</span>
                                {MINUS_PRESETS.map(n => (
                                    <button
                                        key={n}
                                        className={`jd-chip ${minus === n ? 'active' : ''}`}
                                        onClick={() => setMinus(n)}
                                    >{n}</button>
                                ))}
                            </div>
                        </div>

                        {/* Final score */}
                        <div style={{ flex: '0 0 auto', textAlign: 'right', paddingLeft: 24, borderLeft: '1px solid var(--jd-rule)', minWidth: 160 }}>
                            <div className="jd-label">Final score</div>
                            <div style={{ marginTop: 4 }}>
                                <span style={{ fontSize: 44, fontWeight: 600, lineHeight: 1, letterSpacing: '-0.03em' }}>
                                    <span className="jd-hi">{final}</span>
                                </span>
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--jd-ink-3)', marginTop: 4, fontFamily: 'var(--jd-type)' }}>
                                {score} − {minus} = {final}
                            </div>
                        </div>
                    </div>

                    {/* Slider */}
                    <div style={{ marginTop: 16 }}>
                        <ScoreSlider value={score} onChange={setScore} disabled={false} />
                    </div>
                </div>

                {/* Feedback & Comments */}
                <div style={{ display: 'flex', gap: 14, marginTop: 14, alignItems: 'stretch' }}>
                    <div className="jd-box jd-grow" style={{ padding: 16 }}>
                        <div className="jd-row jd-between" style={{ marginBottom: 8 }}>
                            <span className="jd-label">Performance feedback</span>
                            <span className="jd-label" style={{ textTransform: 'none', letterSpacing: 0 }}>{feedback.length} / 600</span>
                        </div>
                        <textarea
                            className="jd-textarea"
                            value={feedback}
                            maxLength={600}
                            onChange={e => setFeedback(e.target.value)}
                            placeholder="Notes on tone, dynamics, expression, technical accuracy…"
                        />
                    </div>
                    <div className="jd-box jd-grow" style={{ padding: 16 }}>
                        <div className="jd-row jd-between" style={{ marginBottom: 8 }}>
                            <span className="jd-label">Comments</span>
                            <span className="jd-label" style={{ textTransform: 'none', letterSpacing: 0 }}>optional</span>
                        </div>
                        <textarea
                            className="jd-textarea"
                            value={comments}
                            onChange={e => setComments(e.target.value)}
                            placeholder="Anything else for the panel…"
                        />
                    </div>
                </div>

                {/* Action bar */}
                <div className="jd-box jd-row jd-between" style={{ padding: '12px 16px', marginTop: 16, background: 'var(--jd-paper-2)' }}>
                    <button className="jd-btn ghost" onClick={onBack}>
                        <IconArrowL size={13} /> Back
                    </button>
                    <div className="jd-row" style={{ gap: 10 }}>
                        {pendingQueue && (
                            <span className="jd-label" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>
                                {pendingQueue.position} of {pendingQueue.total} pending
                            </span>
                        )}
                        <button
                            className="jd-btn"
                            onClick={onNext}
                            disabled={!pendingQueue || pendingQueue.position === pendingQueue.total}
                        >
                            Next participant <IconArrowR size={13} />
                        </button>
                        <button className="jd-btn primary" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : <><IconCheck size={14} /> Save assessment</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssessmentForm;
